import type { ChoiceFormData } from '../types';

// The backend's `ALIAS_CHARS`, which it applies to both the attribute name and each button's value.
const IDENTIFIER_CHARS = /^[A-Za-z0-9_-]+$/;

export type ChoiceErrorKey =
  | 'labelRequired'
  | 'valueRequired'
  | 'valuePattern'
  | 'valueDuplicate'
  | 'dataIdentifierRequired'
  | 'dataIdentifierPattern';

export type ChoiceError =
  | { field: 'dataIdentifier'; key: ChoiceErrorKey }
  | { index: number; field: 'label' | 'value'; key: ChoiceErrorKey };

// A deleted button's fields stay registered under its old index, so a missing index passes rather
// than blocking save.
export const getLabelError = (choice: ChoiceFormData, index: number): ChoiceErrorKey | undefined => {
  const button = choice.buttons[index];
  if (!button) return undefined;
  return button.label.trim() ? undefined : 'labelRequired';
};

export const getValueError = (choice: ChoiceFormData, index: number): ChoiceErrorKey | undefined => {
  const button = choice.buttons[index];
  if (!button || !choice.saveValue) return undefined;
  if (!button.value.trim()) return 'valueRequired';
  // Tested untrimmed: the value is saved exactly as typed.
  if (!IDENTIFIER_CHARS.test(button.value)) return 'valuePattern';
  return choice.buttons.slice(0, index).some(other => other.value === button.value) ? 'valueDuplicate' : undefined;
};

export const getOutputError = (choice: ChoiceFormData): ChoiceErrorKey | undefined => {
  if (!choice.saveValue) return undefined;
  const suffix = choice.dataIdentifier.replace(/^custom\./, '');
  if (!suffix) return 'dataIdentifierRequired';
  return IDENTIFIER_CHARS.test(suffix) ? undefined : 'dataIdentifierPattern';
};

/**
 * The Buttons section unmounts its fields while shut, and react-hook-form skips unmounted fields,
 * so submit runs every rule itself. The attribute is the page's, so it fails before any button.
 */
export const findFirstChoiceError = (choice: ChoiceFormData): ChoiceError | undefined => {
  const output = getOutputError(choice);
  if (output) return { field: 'dataIdentifier', key: output };
  for (const [index] of choice.buttons.entries()) {
    const label = getLabelError(choice, index);
    if (label) return { index, field: 'label', key: label };
    const value = getValueError(choice, index);
    if (value) return { index, field: 'value', key: value };
  }
  return undefined;
};
